import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  changeUWConditionCategoryOrder,
  deleteUWConditionCategory,
  getUWConditionCategories,
} from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button, Input2, Modal, Tooltip } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { UWConditionCateogryDetails } from './details'
import { type IUWConditionCategory, isSystemUwConditionCategory } from './types'

export function UWConditionCategories() {
  const dispatch = useDispatch()
  const [query, setQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [deleteCategory, setDeleteCategory] = useState('')
  const [transferCategory, setTransferCategory] = useState('')

  const categories = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.UWConditionCategories] || [],
  ) as Array<IUWConditionCategory>
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const total = categories.length

  const refresh = () => {
    setIsLoading(true)
    getUWConditionCategories()
      .then(({ data }) => {
        dispatch(setGlobalConfig(GlobalConfigType.UWConditionCategories, data))
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <UWConditionCateogryDetails
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex == -1 ? null : categories[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
      />
    )

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onTrash = async (index: number) => {
    const { title } = categories[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this category?
        <br />
        <span className="text-gray-600">Category title: {title}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setDeleteCategory(title)
    setTransferCategory('')
  }

  const onRemoveAndTransferCategory = async () => {
    if (!transferCategory) {
      toast('Select Transfer Category.', { type: 'error' })
      return
    }
    setIsLoading(true)
    await deleteUWConditionCategory(deleteCategory, transferCategory)
    setDeleteCategory('')
    setTransferCategory('')
    setIsLoading(false)
    refresh()
  }

  const handleOrderChange = async (currentIndex: any, newOrder: any) => {
    const { id } = categories[currentIndex]
    const updatedData = [...categories]
    const [movedItem] = updatedData.splice(currentIndex, 1)
    updatedData.splice(newOrder, 0, movedItem)
    dispatch(setGlobalConfig(GlobalConfigType.UWConditionCategories, updatedData))
    setIsLoading(true)
    await changeUWConditionCategoryOrder(id, newOrder)
    setIsLoading(false)
    refresh()
  }

  return (
    <div className="Notess-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        UW Condition Categories
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {total} categories</p>
        </div>
        <div className="w-32">
          <Button full onClick={onAdd}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Title
              </th>
              <th scope="col" className="px-3">
                Loan Status
              </th>
              <th scope="col" className="px-3">
                Loan Status Restrictions
              </th>
              {query.length === 0 && (
                <th scope="col" className="px-3">
                  Order
                </th>
              )}
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {categories.map((item, index) => {
              const orderOptions = Array.from({ length: categories.length }, (_, i) => i)
              if (query && !`${item.title.toLowerCase()} ${item.notes.toLowerCase()}`.includes(query.toLowerCase()))
                return null

              const loanStatusText = item.loanStatus.map((v: string) => loanGlobalStatus[v]).join(', ')
              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {index + 1}
                  </td>
                  <td className="px-3">
                    <Tooltip message={item.notes}>{item.title}</Tooltip>
                  </td>
                  <td className="px-3">
                    <p className="text-sm text-gray-500 text-ellipsis max-w-sm">{loanStatusText}</p>
                  </td>
                  <td className="px-3">
                    {isSystemUwConditionCategory(item) ? '' : item.addToLoanStatusRestrictions ? 'Yes' : 'No'}
                  </td>
                  {query.length === 0 && (
                    <td className="px-3">
                      <select
                        onChange={(e) => handleOrderChange(index, parseInt(e.target.value))}
                        value={index}
                        className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                      >
                        {orderOptions.map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td className="px-3">
                    <span className="flex">
                      <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => onEdit(index)}>
                        <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onTrash(index)}>
                        <TrashIcon className="w-4 h-4"></TrashIcon>
                      </span>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {deleteCategory && (
        <Modal
          isOpen
          title="Select Transfer Category"
          onClose={() => {
            setDeleteCategory('')
            setTransferCategory('')
          }}
          onOk={onRemoveAndTransferCategory}
        >
          <div className="h-[400px] w-[500px]">
            <RenderInput
              input={{
                inputType: 'select',
                options: categories.map(({ title }) => title).filter((v) => v !== deleteCategory),
                title: 'Select Category',
                value: transferCategory,
                hasDefaultOption: true,
              }}
              Key="transferCategory"
              onChange={(key: string, value: any) => setTransferCategory(value)}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}
